import { IUserProfile } from '../../interfaces/user.interface'
import { NavLink, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getEndUserData } from 'services/setting.service'
import { getPageTitle } from 'utills/page-title'
import { logout } from 'services/auth.service'
import { useLocation } from 'react-router-dom'
import { useLayout } from '../../contexts/LayoutContext'
import { avatar } from 'utills/image'
import { BriefcaseBusiness, Home, Star, MessageCircleMore, Settings, CircleHelp, ArrowLeftFromLine, ChevronsLeft, Sparkles } from 'lucide-react'

function PrivateHeader({ userInfo }: IUserProfile) {
  const { isSidebarCollapsed, setIsSidebarCollapsed, canExpandSidebar, isChatOpen, setIsChatOpen } = useLayout()
  //requires useLocation() to access the current URL pathname smoothly i.e. pageTitle
  const location = useLocation()

  const logoutUser = useMutation({
    mutationFn: () => logout(),
    onSuccess: async () => {
      await localStorage.removeItem('userInfo')
      window.location.reload()
    },
  })

  const handleSubmit = async (ev: React.MouseEvent<HTMLDivElement>) => {
    ev.preventDefault()
    // logoutUser.mutateAsync()
    await localStorage.removeItem('userInfo')
    window.location.reload()
  }

  const handleSidebarToggle = () => {
    // Collapsing is always allowed. Expanding is blocked while chat is open.
    if (!isSidebarCollapsed) {
      setIsSidebarCollapsed(true)
      return
    }
    if (canExpandSidebar) {
      setIsSidebarCollapsed(false)
    }
  }

  const { data: endUserInfo } = useQuery({
    queryKey: ['getEndUserData'],
    queryFn: getEndUserData,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  const hideTopBar = window.location.pathname === '/home'

  return (
    <>
      {!hideTopBar && (
      <nav className={`sticky left-0 top-0 z-40 w-full bg-white border-gray-200 transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'sm:pl-[72px]' : 'sm:pl-[280px]'} ${isChatOpen ? 'sm:pr-[404px]' : ''}`}>
        <div className="h-[81px] px-8 flex items-center justify-between border-b border-[#E8E8E8]">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center justify-start rtl:justify-end">
              <button
                data-drawer-target="logo-sidebar"
                data-drawer-toggle="logo-sidebar"
                aria-controls="logo-sidebar"
                type="button"
                className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg sm:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
                onClick={handleSidebarToggle}
              >
                <span className="sr-only">Open sidebar</span>
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    clipRule="evenodd"
                    fillRule="evenodd"
                    d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                  ></path>
                </svg>
              </button>
              <div className="text-2xl font-semibold capitalize text-brand">
                {getPageTitle(window.location.pathname, endUserInfo?.role || userInfo.role)}
              </div>
            </div>

            <div className="flex items-center">
              <HeaderActions />
              <div className="flex items-center min-h-10">
                <div className="md:hidden">
                  <button
                    hidden
                    type="button"
                    className="flex text-sm bg-gray-800 rounded-full focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600"
                    aria-expanded="false"
                    data-dropdown-toggle="dropdown-user"
                  >
                    <span className="sr-only">Open user menu</span>
                  </button>
                </div>
                <div
                  className={`z-50 md:hidden ${
                    isSidebarCollapsed ? 'block' : 'hidden'
                  } my-4 text-base list-none bg-white divide-y divide-gray-100 rounded shadow dark:bg-gray-700 dark:divide-gray-600 ${
                    isSidebarCollapsed ? 'absolute left-0' : 'fixed'
                  }`}
                  id="dropdown-user"
                >
                  <div className="px-4 py-3" role="none">
                    <p
                      className="text-sm text-gray-900 dark:text-white"
                      role="none"
                    >
                      {userInfo.full_name}
                    </p>
                    <p
                      className="text-sm font-medium text-gray-900 truncate dark:text-gray-300"
                      role="none"
                    >
                      {userInfo.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
      )}

      <aside
        id="logo-sidebar"
        className={`${
          !isSidebarCollapsed ? 'translate-x-0 w-[280px]' : '-translate-x-full w-[72px]'
        } fixed top-0 left-0 z-50 h-screen transition-all duration-300 bg-brand border-brand sm:translate-x-0 ${isSidebarCollapsed ? 'sm:w-[72px]' : 'sm:w-[280px]'}`}
        aria-label="Sidebar"
      >
        <div className={`h-full bg-brand text-sm font-normal ${isSidebarCollapsed ? 'px-2 py-4' : 'p-4'}`}>
          <div className="flex flex-col justify-between h-full">
            <div>
              <div className="pt-2">
                {!isSidebarCollapsed ? (
                  <div className="flex items-center justify-between h-[24px]">
                    <a href="/" className="flex">
                      <img
                        src="/assets/brand/CareerSight Logo Web-03.png"
                        className="w-[146px] h-[16px]"
                        alt="CareerSight Logo"
                      />
                    </a>
                    <button
                      type="button"
                      aria-label="Collapse sidebar"
                      onClick={handleSidebarToggle}
                      className="cursor-pointer hover:bg-white/10 rounded-lg p-1 opacity-80"
                    >
                      <ChevronsLeft size={16} stroke="white" />
                    </button>
                  </div>
                ) : (
                  <a href="/" className="flex items-center justify-center h-[24px]"
                    onClick={(e) => {
                      e.preventDefault();
                      if (isChatOpen) {
                        setIsChatOpen(false);
                      }
                      setIsSidebarCollapsed(false);
                    }}
                  >
                    <img
                      src="/assets/brand/CareerSight Logo Web-02.png"
                      className="h-[24px] w-auto"
                      alt="CareerSight Logo"
                    />
                  </a>
                )}
              </div>
              <div className={`pt-6 space-y-2 ${isSidebarCollapsed ? 'flex flex-col items-center' : ''}`}>
                {userInfo.role === 'super' && (
                  <>
                    <NavLink
                      to="/admin-dashboard"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Overview' : undefined}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        className="w-6 h-6"
                      >
                        <path
                          d="M6.62534 1H2.12534C1.50402 1 1.00034 1.50368 1.00034 2.125V6.625C1.00034 7.24632 1.50402 7.75 2.12534 7.75H6.62534C7.24666 7.75 7.75034 7.24632 7.75034 6.625V2.125C7.75034 1.50368 7.24666 1 6.62534 1Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.8749 1H13.3749C12.7536 1 12.2499 1.50368 12.2499 2.125V6.625C12.2499 7.24632 12.7536 7.75 13.3749 7.75H17.8749C18.4962 7.75 18.9999 7.24632 18.9999 6.625V2.125C18.9999 1.50368 18.4962 1 17.8749 1Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.625 12.2502H2.125C1.50368 12.2502 1 12.7539 1 13.3752V17.8752C1 18.4966 1.50368 19.0002 2.125 19.0002H6.625C7.24632 19.0002 7.75 18.4966 7.75 17.8752V13.3752C7.75 12.7539 7.24632 12.2502 6.625 12.2502Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.8749 12.2502H13.3749C12.7536 12.2502 12.2499 12.7539 12.2499 13.3752V17.8752C12.2499 18.4966 12.7536 19.0002 13.3749 19.0002H17.8749C18.4962 19.0002 18.9999 18.4966 18.9999 17.8752V13.3752C18.9999 12.7539 18.4962 12.2502 17.8749 12.2502Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {!isSidebarCollapsed && <span className="ms-3">Overview</span>}
                    </NavLink>
                    <NavLink
                      to="/partners"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Partners' : undefined}
                    >
                      <svg
                        width={20}
                        height={20}
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_4641_43902)">
                          <path
                            d="M10 11.6667C9.91667 11.6667 9.83333 11.6667 9.75 11.5834L0.583333 8.25002C0.25 8.16669 0 7.83335 0 7.50002C0 7.16669 0.25 6.83335 0.583333 6.75002L9.75 3.41669C9.91667 3.33335 10.1667 3.33335 10.3333 3.41669L19.5 6.75002C19.75 6.83335 20 7.16669 20 7.50002C20 7.83335 19.75 8.16669 19.4167 8.25002L10.25 11.5834C10.1667 11.6667 10.0833 11.6667 10 11.6667ZM3.25 7.50002L10 9.91669L16.75 7.50002L10 5.08335L3.25 7.50002Z"
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                          />
                          <path
                            d="M15.8333 16.6667H10C9.5 16.6667 9.16666 16.3333 9.16666 15.8333C9.16666 15.3333 9.5 15 10 15H15C14.9167 12.8333 14.4167 11 13.5833 9.66668C13.3333 9.25001 13.5 8.75001 13.8333 8.50001C14.25 8.25001 14.75 8.41668 15 8.75001C16.0833 10.5 16.6667 12.9167 16.6667 15.75C16.6667 16.3333 16.3333 16.6667 15.8333 16.6667Z"
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                          />
                          <path
                            d="M10 16.6667H4.16667C3.66667 16.6667 3.33334 16.3334 3.33334 15.8334C3.33334 13 3.91667 10.5834 5 8.83335C5.25 8.41669 5.75 8.33335 6.16667 8.58335C6.58334 8.83335 6.66667 9.33335 6.41667 9.75002C5.58334 11.0834 5.08334 12.9167 5 15.0834H10C10.5 15.0834 10.8333 15.4167 10.8333 15.9167C10.8333 16.4167 10.5 16.6667 10 16.6667ZM18.3333 16.6667C17.8333 16.6667 17.5 16.3334 17.5 15.8334V7.50002C17.5 7.00002 17.8333 6.66669 18.3333 6.66669C18.8333 6.66669 19.1667 7.00002 19.1667 7.50002V15.8334C19.1667 16.3334 18.8333 16.6667 18.3333 16.6667Z"
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_4641_43902">
                            <rect width={20} height={20} fill="white" />
                          </clipPath>
                        </defs>
                      </svg>

                      {!isSidebarCollapsed && <span className="ms-3">Partners</span>}
                    </NavLink>
                    <NavLink
                      to="/individuals"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Individuals' : undefined}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="20"
                        viewBox="0 0 16 20"
                        fill="none"
                        className="w-6 h-6"
                      >
                        <path
                          d="M7.75038 7.75C9.61434 7.75 11.1254 6.23896 11.1254 4.375C11.1254 2.51104 9.61434 1 7.75038 1C5.88642 1 4.37538 2.51104 4.37538 4.375C4.37538 6.23896 5.88642 7.75 7.75038 7.75Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.50011 11.125H10.0001C12.4835 11.125 14.5001 13.1416 14.5001 15.625V16.75C14.5001 17.9931 13.4932 19 12.2501 19H3.25011C2.00698 19 1.00011 17.9931 1.00011 16.75V15.625C1.00011 13.1416 3.01667 11.125 5.50011 11.125Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {!isSidebarCollapsed && <span className="ms-3">Individuals</span>}
                    </NavLink>
                    <NavLink
                      to="/careers"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Careers' : undefined}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                      >
                        <path
                          d="M19.6669 6.49994H3.33357C2.04491 6.49994 1.00024 7.54461 1.00024 8.83327V19.3333C1.00024 20.6219 2.04491 21.6666 3.33357 21.6666H19.6669C20.9556 21.6666 22.0002 20.6219 22.0002 19.3333V8.83327C22.0002 7.54461 20.9556 6.49994 19.6669 6.49994Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.00034 6.5V5.33333C8.00034 4.04417 9.0445 3 10.3337 3H12.667C13.9562 3 15.0003 4.04417 15.0003 5.33333V6.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1.00024 11.1667C1.00024 13.1005 2.56649 14.6667 4.50024 14.6667H18.5002C20.434 14.6667 22.0002 13.1005 22.0002 11.1667"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      {!isSidebarCollapsed && <span className="ms-3">Careers</span>}
                    </NavLink>
                  </>
                )}
                {userInfo.role === 'user' && (
                  <>
                    <NavLink
                      to="/home"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Home' : undefined}
                    >
                      <Home size={20} stroke="white" />
                      {!isSidebarCollapsed && <span className="ms-3">Home</span>}
                    </NavLink>
                    <NavLink
                      to="/user-careers?tab=preview"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Careers' : undefined}
                    >
                      <BriefcaseBusiness size={20} stroke="white" />
                      {!isSidebarCollapsed && <span className="ms-3">Careers</span>}
                    </NavLink>
                    <NavLink
                      to="/career-awareness"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'My Career Awareness' : undefined}
                    >
                      <Star size={20} stroke="white" />
                      {!isSidebarCollapsed && <span className="ms-3">My Career Awareness</span>}
                    </NavLink>
                  </>
                )}
                {userInfo.role === 'partner' && (
                  <>
                    <NavLink
                      to="/partner-dashboard"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Dashboard' : undefined}
                    >
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.62534 3H4.12534C3.50402 3 3.00034 3.50368 3.00034 4.125V8.625C3.00034 9.24632 3.50402 9.75 4.12534 9.75H8.62534C9.24666 9.75 9.75034 9.24632 9.75034 8.625V4.125C9.75034 3.50368 9.24666 3 8.62534 3Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M19.8749 3H15.3749C14.7536 3 14.2499 3.50368 14.2499 4.125V8.625C14.2499 9.24632 14.7536 9.75 15.3749 9.75H19.8749C20.4963 9.75 20.9999 9.24632 20.9999 8.625V4.125C20.9999 3.50368 20.4963 3 19.8749 3Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.625 14.2502H4.125C3.50368 14.2502 3 14.7539 3 15.3752V19.8752C3 20.4966 3.50368 21.0002 4.125 21.0002H8.625C9.24632 21.0002 9.75 20.4966 9.75 19.8752V15.3752C9.75 14.7539 9.24632 14.2502 8.625 14.2502Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M19.8749 14.2502H15.3749C14.7536 14.2502 14.2499 14.7539 14.2499 15.3752V19.8752C14.2499 20.4966 14.7536 21.0002 15.3749 21.0002H19.8749C20.4963 21.0002 20.9999 20.4966 20.9999 19.8752V15.3752C20.9999 14.7539 20.4963 14.2502 19.8749 14.2502Z"
                          stroke="white"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      {!isSidebarCollapsed && <span className="ms-3">Dashboard</span>}
                    </NavLink>
                    <NavLink
                      to="/partner-careers"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Careers' : undefined}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                      >
                        <path
                          d="M19.6669 6.49994H3.33357C2.04491 6.49994 1.00024 7.54461 1.00024 8.83327V19.3333C1.00024 20.6219 2.04491 21.6666 3.33357 21.6666H19.6669C20.9556 21.6666 22.0002 20.6219 22.0002 19.3333V8.83327C22.0002 7.54461 20.9556 6.49994 19.6669 6.49994Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8.00034 6.5V5.33333C8.00034 4.04417 9.0445 3 10.3337 3H12.667C13.9562 3 15.0003 4.04417 15.0003 5.33333V6.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1.00024 11.1667C1.00024 13.1005 2.56649 14.6667 4.50024 14.6667H18.5002C20.434 14.6667 22.0002 13.1005 22.0002 11.1667"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                      {!isSidebarCollapsed && <span className="ms-3">Careers</span>}
                    </NavLink>
                    <NavLink
                      to="/coaches-corner"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Coaches Corner' : undefined}
                    >
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M2.43375 10.824C1.8405 11.8282 1.5 12.9998 1.5 14.25C1.5 17.9753 4.52475 21 8.25 21C11.7218 21 14.5852 18.3728 14.9587 15H21C21.378 15 21.6975 14.718 21.744 14.343L22.494 8.343C22.521 8.12925 22.455 7.91475 22.3125 7.7535C22.17 7.59225 21.9653 7.5 21.75 7.5H15C14.7157 7.5 14.4563 7.6605 14.3295 7.91475L13.7865 9H11.7135L11.1705 7.91475C11.0437 7.6605 10.7843 7.5 10.5 7.5H8.25C7.36875 7.5 6.5265 7.6695 5.754 7.977C5.38125 7.24875 4.62375 6.75 3.75 6.75C2.508 6.75 1.5 7.758 1.5 9C1.5 9.75075 1.86825 10.4153 2.43375 10.824ZM8.25 9H10.0365L10.5795 10.0852C10.7063 10.3395 10.9657 10.5 11.25 10.5H14.25C14.5343 10.5 14.7937 10.3395 14.9205 10.0852L15.4635 9H20.9002L20.3377 13.5H14.25C13.836 13.5 13.5 13.836 13.5 14.25C13.5 17.1473 11.1473 19.5 8.25 19.5C5.35275 19.5 3 17.1473 3 14.25C3 11.3527 5.35275 9 8.25 9ZM8.25 10.5C6.18 10.5 4.5 12.18 4.5 14.25C4.5 16.32 6.18 18 8.25 18C10.32 18 12 16.32 12 14.25C12 12.18 10.32 10.5 8.25 10.5ZM8.25 12C9.492 12 10.5 13.008 10.5 14.25C10.5 15.492 9.492 16.5 8.25 16.5C7.008 16.5 6 15.492 6 14.25C6 13.008 7.008 12 8.25 12ZM4.431 8.685C4.31175 8.4285 4.0515 8.25 3.75 8.25C3.336 8.25 3 8.586 3 9C3 9.261 3.1335 9.4905 3.33525 9.62475C3.66525 9.2745 4.03275 8.95875 4.431 8.685ZM14.871 4.31775L13.8105 5.379C13.518 5.6715 13.518 6.147 13.8105 6.4395C14.103 6.732 14.5785 6.732 14.871 6.4395L15.9323 5.379C16.2247 5.08575 16.2247 4.611 15.9323 4.31775C15.639 4.02525 15.1642 4.02525 14.871 4.31775ZM9.56775 5.379L10.629 6.4395C10.9215 6.732 11.397 6.732 11.6895 6.4395C11.982 6.147 11.982 5.6715 11.6895 5.379L10.629 4.31775C10.3357 4.02525 9.861 4.02525 9.56775 4.31775C9.27525 4.611 9.27525 5.08575 9.56775 5.379ZM12 3.75V5.25C12 5.664 12.336 6 12.75 6C13.164 6 13.5 5.664 13.5 5.25V3.75C13.5 3.336 13.164 3 12.75 3C12.336 3 12 3.336 12 3.75Z"
                          fill="white"
                          stroke="white"
                          strokeWidth="0.25"
                        />
                      </svg>

                      {!isSidebarCollapsed && <span className="ms-3">Coaches Corner</span>}
                    </NavLink>
                  </>
                )}
              </div>
            </div>
            <div>
              <div className={`space-y-1 font-medium ${isSidebarCollapsed ? 'flex flex-col items-center' : ''}`}>
                {userInfo.role === 'super' && (
                  <>
                    <NavLink
                      to="/media-content"
                      className={({ isActive, isPending }) =>
                        [
                          isPending ? 'pending ' : '',
                          isActive ? 'rounded-lg bg-white/10 ' : '',
                          isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                        ].join('menu ')
                      }
                      title={isSidebarCollapsed ? 'Uploads' : undefined}
                    >
                      <svg
                        version="1.1"
                        id="Uploaded to svgrepo.com"
                        xmlns="http://www.w3.org/2000/svg"
                        width="20px"
                        height="20"
                        viewBox="0 0 32 32"
                        fill="#ffffff"
                      >
                        <path
                          className="feather_een"
                          d="M27.257,4.097l-16.331,6.534C10.779,10.262,10.422,10,10,10H5c-0.552,0-1,0.448-1,1v0H3
	c-1.105,0-2,0.895-2,2v4c0,1.105,0.895,2,2,2l1,0c0,0.552,0.448,1,1,1v2c0,1.657,1.343,3,3,3h4c1.657,0,3-1.343,3-3v-1l12.257,4.903
	C28.571,26.428,30,25.461,30,24.046V5.954C30,4.539,28.571,3.572,27.257,4.097z M4,18H3c-0.552,0-1-0.448-1-1v-4
	c0-0.552,0.448-1,1-1h1V18z M10.925,19.371L12,19.8V22H8l0-2h2C10.421,20,10.777,19.739,10.925,19.371z M5,11h5v8l-5,0V11z M12,24H8
	c-1.105,0-2-0.895-2-2v-2h1l0,2c0,0.552,0.448,1,1,1h4c0.552,0,1-0.448,1-1v-1.8l1,0.4V22C14,23.105,13.104,24,12,24z
	 M27.629,24.974L11,18.323v-6.646l16.629-6.651C28.285,4.763,29,5.247,29,5.954v18.092C29,24.753,28.285,25.237,27.629,24.974z"
                        />
                      </svg>
                      {!isSidebarCollapsed && <span className="ms-3">Uploads</span>}
                    </NavLink>
                  </>
                )}
                {userInfo.role === 'super' && (
                  <NavLink
                    to="/promo-codes"
                    className={({ isActive, isPending }) =>
                      [
                        isPending ? 'pending ' : '',
                        isActive ? 'rounded-lg bg-white/10 ' : '',
                        isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                      ].join('menu ')
                    }
                    title={isSidebarCollapsed ? 'Discount Codes' : undefined}
                  >
                    <svg
                      fill="#ffffff"
                      height="20px"
                      width="20px"
                      version="1.1"
                      id="Layer_1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      stroke="#ffffff"
                      strokeWidth="20.48"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke="#CCCCCC"
                        strokeWidth="15.36"
                      >
                        {' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M495.432,111.165H384.267c-4.427,0-8.017,3.589-8.017,8.017v8.551c0,5.01-4.076,9.086-9.086,9.086 c-5.01,0-9.086-4.076-9.086-9.086v-8.551c0-4.427-3.589-8.017-8.017-8.017H16.568C7.432,111.165,0,118.597,0,127.733v256.534 c0,9.136,7.432,16.568,16.568,16.568h333.495c4.427,0,8.017-3.589,8.017-8.017v-8.551c0-5.01,4.076-9.086,9.086-9.086 c5.01,0,9.086,4.076,9.086,9.086v8.551c0,4.427,3.589,8.017,8.017,8.017h111.165c9.136,0,16.568-7.432,16.568-16.568V127.733 C512,118.597,504.568,111.165,495.432,111.165z M495.967,384.267c0,0.295-0.239,0.534-0.534,0.534H392.284v-0.534 c0-11.048-7.172-20.446-17.102-23.8v-10.405c0-4.427-3.589-8.017-8.017-8.017c-4.427,0-8.017,3.589-8.017,8.017v10.405 c-9.93,3.354-17.102,12.752-17.102,23.8v0.534H16.568c-0.295,0-0.534-0.239-0.534-0.534V127.733c0-0.295,0.239-0.534,0.534-0.534 h325.478v0.534c0,11.048,7.172,20.446,17.102,23.8v10.405c0,4.427,3.589,8.017,8.017,8.017c4.427,0,8.017-3.589,8.017-8.017 v-10.405c9.93-3.354,17.102-12.752,17.102-23.8v-0.534h103.148c0.295,0,0.534,0.239,0.534,0.534V384.267z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M367.165,188.125c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,191.715,371.592,188.125,367.165,188.125z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M367.165,239.432c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,243.021,371.592,239.432,367.165,239.432z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M367.165,290.739c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,294.328,371.592,290.739,367.165,290.739z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M261.668,173.371c-3.131-3.131-8.207-3.131-11.337,0L96.411,327.292c-3.131,3.131-3.131,8.207,0,11.337 c1.565,1.566,3.617,2.348,5.668,2.348s4.103-0.782,5.668-2.348l153.921-153.921C264.799,181.578,264.799,176.501,261.668,173.371z "></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M127.733,162.472c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S151.013,162.472,127.733,162.472z M127.733,230.881c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S142.172,230.881,127.733,230.881z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M230.347,265.086c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S253.627,265.086,230.347,265.086z M230.347,333.495c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S244.786,333.495,230.347,333.495z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                      </g>
                      <g id="SVGRepo_iconCarrier">
                        {' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M495.432,111.165H384.267c-4.427,0-8.017,3.589-8.017,8.017v8.551c0,5.01-4.076,9.086-9.086,9.086 c-5.01,0-9.086-4.076-9.086-9.086v-8.551c0-4.427-3.589-8.017-8.017-8.017H16.568C7.432,111.165,0,118.597,0,127.733v256.534 c0,9.136,7.432,16.568,16.568,16.568h333.495c4.427,0,8.017-3.589,8.017-8.017v-8.551c0-5.01,4.076-9.086,9.086-9.086 c5.01,0,9.086,4.076,9.086,9.086v8.551c0,4.427,3.589,8.017,8.017,8.017h111.165c9.136,0,16.568-7.432,16.568-16.568V127.733 C512,118.597,504.568,111.165,495.432,111.165z M495.967,384.267c0,0.295-0.239,0.534-0.534,0.534H392.284v-0.534 c0-11.048-7.172-20.446-17.102-23.8v-10.405c0-4.427-3.589-8.017-8.017-8.017c-4.427,0-8.017,3.589-8.017,8.017v10.405 c-9.93,3.354-17.102,12.752-17.102,23.8v0.534H16.568c-0.295,0-0.534-0.239-0.534-0.534V127.733c0-0.295,0.239-0.534,0.534-0.534 h325.478v0.534c0,11.048,7.172,20.446,17.102,23.8v10.405c0,4.427,3.589,8.017,8.017,8.017c4.427,0,8.017-3.589,8.017-8.017 v-10.405c9.93-3.354,17.102-12.752,17.102-23.8v-0.534h103.148c0.295,0,0.534,0.239,0.534,0.534V384.267z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M367.165,188.125c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,191.715,371.592,188.125,367.165,188.125z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M367.165,239.432c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,243.021,371.592,239.432,367.165,239.432z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M367.165,290.739c-4.427,0-8.017,3.589-8.017,8.017v17.102c0,4.427,3.589,8.017,8.017,8.017 c4.427,0,8.017-3.589,8.017-8.017v-17.102C375.182,294.328,371.592,290.739,367.165,290.739z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M261.668,173.371c-3.131-3.131-8.207-3.131-11.337,0L96.411,327.292c-3.131,3.131-3.131,8.207,0,11.337 c1.565,1.566,3.617,2.348,5.668,2.348s4.103-0.782,5.668-2.348l153.921-153.921C264.799,181.578,264.799,176.501,261.668,173.371z "></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M127.733,162.472c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S151.013,162.472,127.733,162.472z M127.733,230.881c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S142.172,230.881,127.733,230.881z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                        <g>
                          {' '}
                          <g>
                            {' '}
                            <path d="M230.347,265.086c-23.281,0-42.221,18.941-42.221,42.221s18.941,42.221,42.221,42.221s42.221-18.941,42.221-42.221 S253.627,265.086,230.347,265.086z M230.347,333.495c-14.44,0-26.188-11.748-26.188-26.188s11.748-26.188,26.188-26.188 s26.188,11.748,26.188,26.188S244.786,333.495,230.347,333.495z"></path>{' '}
                          </g>{' '}
                        </g>{' '}
                      </g>
                    </svg>

                    {!isSidebarCollapsed && <span className="ms-3">Discount Codes</span>}
                  </NavLink>
                )}
                <NavLink
                  to="/chats"
                  className={({ isActive, isPending }) =>
                    [
                      isPending ? 'pending ' : '',
                      isActive ? 'rounded-lg bg-white/10 ' : '',
                      isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                    ].join('menu ')
                  }
                  title={isSidebarCollapsed ? 'Chats' : undefined}
                >
                  <MessageCircleMore size={20} stroke="white" />
                  {!isSidebarCollapsed && <span className="ms-3">Chats</span>}
                </NavLink>
                <NavLink
                  to="/settings"
                  className={({ isActive, isPending }) =>
                    [
                      isPending ? 'pending ' : '',
                      isActive ? 'rounded-lg bg-white/10 ' : '',
                      isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                    ].join('menu ')
                  }
                  title={isSidebarCollapsed ? 'Settings' : undefined}
                >
                  <Settings size={20} stroke="white" />
                  {!isSidebarCollapsed && <span className="ms-3">Settings</span>}
                </NavLink>
                {userInfo.role !== 'super' && (
                  <NavLink
                    to="/help-center"
                    className={({ isActive, isPending }) =>
                      [
                        isPending ? 'pending ' : '',
                        isActive ? 'rounded-lg bg-white/10 ' : '',
                        isSidebarCollapsed ? 'justify-center w-8 h-8 ' : '',
                      ].join('menu ')
                    }
                    title={isSidebarCollapsed ? 'Help Center' : undefined}
                  >
                    <CircleHelp size={20} stroke="white" />
                    {!isSidebarCollapsed && <span className="ms-3">Help Center</span>}
                  </NavLink>
                )}

                <div
                  className={`cursor-pointer menu ${isSidebarCollapsed ? 'justify-center w-8 h-8' : ''}`}
                  title={isSidebarCollapsed ? 'Log Out' : ''}
                  onClick={(ev: React.MouseEvent<HTMLDivElement>) =>
                    handleSubmit(ev)
                  }
                >
                  <ArrowLeftFromLine size={20} stroke="white" />
                  {!isSidebarCollapsed && <span className="ms-3">Log Out</span>}
                </div>
              </div>
              <div className={`flex items-center py-5 truncate ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                {endUserInfo && (
                  <img
                    className="w-10 h-10 rounded-full"
                   src={
                      endUserInfo?.profile?.avatar ||
                      userInfo.avatar ||
                      avatar[endUserInfo?.role as keyof typeof avatar] ||
                      avatar[userInfo.role as keyof typeof avatar] ||
                      avatar.default
                    }
                    alt="user_photo"
                  />
                )}
                {!isSidebarCollapsed && (
                  <div>
                    <p className="text-sm font-semibold text-white capitalize ms-3">
                      {endUserInfo && endUserInfo.role === 'user'
                        ? `${endUserInfo?.profile.first_name} ${endUserInfo?.profile.last_name}`
                        : `${userInfo.full_name}`}
                    </p>
                    <p
                      className="text-xs font-normal text-white truncate ms-3 max-w-xs whitespace-nowrap overflow-hidden"
                      title={userInfo.email}
                    >
                      {userInfo.email}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}

function HeaderActions() {
  const {
    isChatOpen,
    setIsChatOpen,
    careersReorderMode,
    setCareersReorderMode,
    triggerCareersSave,
  } = useLayout()
  const isCareersPage = window.location.pathname === '/user-careers'

  if (isCareersPage && careersReorderMode) {
    return (
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setCareersReorderMode(false)}
          className="font-sans text-sm font-semibold px-5 py-2 rounded-full bg-[#E8E8E8] text-gray-dark hover:opacity-90"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={triggerCareersSave}
          className="font-sans text-sm font-semibold px-5 py-2 rounded-full text-white hover:opacity-90"
          style={{ background: '#2B8565' }}
        >
          Save Changes
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      {isCareersPage && (
        <button
          type="button"
          onClick={() => setCareersReorderMode(true)}
          className="font-sans text-sm font-semibold px-5 py-2 rounded-full bg-[#E8E8E8] text-gray-dark hover:opacity-90"
        >
          Reorder
        </button>
      )}
      {!isChatOpen && (
        <button
          type="button"
          onClick={() => setIsChatOpen(true)}
          className="flex items-center gap-2 font-sans text-sm font-semibold px-5 py-2 rounded-full text-white hover:opacity-90"
          style={{ background: '#2B8565' }}
        >
          <Sparkles size={16} />
          Explore with Career Sherpa
        </button>
      )}
    </div>
  )
}

export default PrivateHeader
